/* eslint-disable @stylistic/quote-props */
import { Array, Data, DateTime, Effect, pipe, Record } from 'effect'
import type { Users } from '~/auth/known-users'
import type { advent24Rounds } from '~/models/submission'

const makeDate = (day: number) => DateTime.unsafeMakeZoned({
  day,
  month: 12,
  year: 2024,
}, {
  adjustForTimeZone: true,
  timeZone: 'Europe/Copenhagen',
})

// 1, 8, 15, 22, 24, 31 december?
export const cutoffs = ({
  '1': makeDate(1),
  '2': makeDate(8),
  '3': makeDate(15),
  '4': makeDate(22),
  '5': makeDate(24),
  '6': makeDate(31),
} as const) satisfies {
  [key in typeof advent24Rounds[number]]: DateTime.DateTime
}

export const advent24RoundUsers = ({
  '1': 'don_domingo',
  '2': 'bigcupping',
  '3': 'jesper',
  '4': 'nicholai',
  '5': 'jacobsen',
  '6': 's1n1sta',
} as const) satisfies {
  [key in typeof advent24Rounds[number]]: keyof typeof Users
}

export class LateSubmissionError extends Data.TaggedError('LateSubmission')<{
  message: string
}> {}

export const afterCutoffGuard = (date: keyof typeof cutoffs) => <T>(input: T) => pipe(
  DateTime.isPast(cutoffs[date]),
  Effect.flatMap(past => past
    ? Effect.fail(new LateSubmissionError({ message: 'Submission late' }))
    : Effect.succeed(input),
  ),
)

export class EarlyRevealError extends Data.TaggedError('EarlyReveal')<{
  message: string
}> {}

export const beforeCutoffGuard = (date: keyof typeof cutoffs) => pipe(
  DateTime.isFuture(cutoffs[date]),
  Effect.flatMap(future => future
    ? Effect.fail(new EarlyRevealError({ message: 'Reveal is too early ' }))
    : Effect.succeed(date),
  ),
)

export class RoundOutOfBoundsError extends Data.TaggedError('RoundOutOfBounds')<{
  message: string
}> {}

const isValidRound = (round: string): round is keyof typeof cutoffs => Array.contains(Record.keys(cutoffs), round)
export const roundBoundGuard = (round: string): Effect.Effect<keyof typeof cutoffs, RoundOutOfBoundsError> => isValidRound(round)
  ? Effect.succeed(round)
  : Effect.fail(new RoundOutOfBoundsError({ message: `${round} is out of bounds (1-6)` }))
